import React, { useState, useEffect, useImperativeHandle } from 'react';
import { Plus, Trash2, Syringe, ClipboardPaste } from 'lucide-react';
import { useProtocoloSession } from '../../../../hooks/useProtocoloSession';
import BuscaDeAnimal from '../../shared/BuscaDeAnimal';
import { moverLinha } from '../../../../utils/moverLinha';
import { useArrastarLinhas, AlcaDeArrastar } from '../../shared/useArrastarLinhas';
import EquipeDaEtapa, { equipeNoWorkflow } from '../../shared/EquipeDaEtapa';
import {
  EMPTY_PROTOCOLO_SESSION, EMPTY_RECEPTORA, EMPTY_APLICACAO,
  PROTOCOLOS_RECEPTORA, HORMONIOS_SUGERIDOS, MOMENTOS_DO_PROTOCOLO,
  cronogramaPadrao, dataDaRetirada, generateTempId,
} from '../../../../constants/workflowGranular';
import { MARK } from '../../../../constants';
import { descreverPerda, confirmarRemocao } from '../../../../utils/confirmarRemocao';
import { sessaoDoLocal } from '../../../../data/clients';

/**
 * Aba PROTOCOLO — a visita em que se escolhem as receptoras e começa a
 * sincronização hormonal.
 *
 * É a primeira etapa da corrente: a OPU é agendada oito dias depois do
 * implante. Aqui nascem as receptoras que vão aparecer na TE, no DG30 e no
 * DG60 — e os relatórios de visita 1, 2 e 4 saem inteiros desta tela.
 */
const ProtocoloTab = React.forwardRef(({
  service, workflowData, onUpdate, laboratoryId, clients = [],
}, ref) => {
  const { loadProtocoloData, saveProtocoloData } = useProtocoloSession(laboratoryId);

  const [dados, setDados] = useState(EMPTY_PROTOCOLO_SESSION);
  // A ordem das receptoras é o sort_order que o salvamento grava.
  const arrastar = useArrastarLinhas((de, para) => setDados(a => ({
    ...a, receptoras: moverLinha(a.receptoras, de, para),
  })));
  const [isInitialized, setIsInitialized] = useState(false);
  const equipe = equipeNoWorkflow(workflowData, onUpdate, 'protocolo');
  const [colando, setColando] = useState(false);
  const [texto, setTexto] = useState('');

  useEffect(() => {
    if (isInitialized || !laboratoryId || !service?.id) return;
    let cancelado = false;

    (async () => {
      const salvo = await loadProtocoloData(service.id);
      if (cancelado) return;
      setDados(salvo || {
        ...EMPTY_PROTOCOLO_SESSION,
        session: {
          ...EMPTY_PROTOCOLO_SESSION.session,
          // Prefill do que o serviço já sabe: quem abre a aba em campo não
          // deveria redigitar a fazenda que está no cadastro.
          date: workflowData?.protocolo_date || service?.protocolo_scheduled_date || null,
          ...sessaoDoLocal(service, clients),
        },
      });
      setIsInitialized(true);
    })();

    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.id, laboratoryId, isInitialized]);

  const ativas = (dados.receptoras || []).filter(r => r.ativa !== false);

  useEffect(() => {
    if (!isInitialized) return;
    onUpdate('protocolo_date', dados.session?.date || null);
    onUpdate('protocolo_done', dados.completed || false);
    onUpdate('protocolo_quantity', ativas.length);
    onUpdate('protocolo_observations', dados.observations || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados, isInitialized]);

  useImperativeHandle(ref, () => ({
    save: () => {
      if (!isInitialized || !service?.id) return Promise.resolve();
      return saveProtocoloData(service.id, dados);
    },
  }), [isInitialized, dados, service?.id, saveProtocoloData]);

  // ── Sessão ───────────────────────────────────────────────────────────────
  const mudarSessao = (campo, valor) =>
    setDados(a => ({ ...a, session: { ...a.session, [campo]: valor } }));

  // ── Receptoras ───────────────────────────────────────────────────────────
  const mudarReceptora = (id, campo, valor) =>
    setDados(a => ({
      ...a,
      receptoras: a.receptoras.map(r => (r.id === id ? { ...r, [campo]: valor } : r)),
    }));

  const escolherAnimal = (id, animal) =>
    setDados(a => ({
      ...a,
      receptoras: a.receptoras.map(r => (r.id === id
        ? { ...r, animal_id: animal.id, identifier: animal.identifier, breed: animal.breed || '' }
        : r)),
    }));

  /** Digitar por cima solta o vínculo, senão o id da anterior ficaria. */
  const digitarBrinco = (id, texto) =>
    setDados(a => ({
      ...a,
      receptoras: a.receptoras.map(r => (r.id === id
        ? { ...r, identifier: texto, animal_id: null } : r)),
    }));

  const acrescentar = (quantas = 1) =>
    setDados(a => ({
      ...a,
      receptoras: [
        ...a.receptoras,
        ...Array.from({ length: quantas }, () => ({ ...EMPTY_RECEPTORA, id: generateTempId() })),
      ],
    }));

  const remover = (id) => {
    const r = dados.receptoras.find(x => x.id === id);
    const perdas = [];
    if ((r?.identifier || '').trim()) perdas.push(`brinco ${r.identifier}`);
    if ((r?.observations || '').trim()) perdas.push('observações');

    if (!confirmarRemocao(descreverPerda({ nome: r?.identifier, perdas }))) return;
    setDados(a => ({ ...a, receptoras: a.receptoras.filter(x => x.id !== id) }));
  };

  /**
   * Colar a lista de brincos de uma vez.
   *
   * Trinta linhas digitadas uma a uma é o que faz a pessoa voltar para o
   * caderno. O produtor manda a lista por mensagem; isto a transforma em
   * tabela.
   */
  const colarLista = () => {
    const brincos = texto.split('\n').map(l => l.trim()).filter(Boolean);
    if (brincos.length === 0) { setColando(false); return; }
    setDados(a => ({
      ...a,
      receptoras: [
        ...a.receptoras.filter(r => (r.identifier || '').trim() || r.animal_id),
        ...brincos.map(b => ({ ...EMPTY_RECEPTORA, id: generateTempId(), identifier: b })),
      ],
    }));
    setTexto('');
    setColando(false);
  };

  // ── Cronograma ───────────────────────────────────────────────────────────
  const mudarAplicacao = (id, campo, valor) =>
    setDados(a => ({
      ...a,
      aplicacoes: a.aplicacoes.map(x => (x.id === id ? { ...x, [campo]: valor } : x)),
    }));

  const acrescentarAplicacao = () =>
    setDados(a => ({
      ...a,
      aplicacoes: [...a.aplicacoes, { ...EMPTY_APLICACAO, id: generateTempId() }],
    }));

  const removerAplicacao = (id) =>
    setDados(a => ({ ...a, aplicacoes: a.aplicacoes.filter(x => x.id !== id) }));

  const gerarCronograma = () =>
    setDados(a => ({
      ...a,
      aplicacoes: cronogramaPadrao(a.session?.date, a.session?.technician_name || '')
        .map(x => ({ ...x, id: generateTempId() })),
    }));

  const retirada = dataDaRetirada(dados.aplicacoes, dados.session?.date);

  if (!isInitialized) {
    return <div className="p-8 text-center text-sm text-slate-500">Carregando...</div>;
  }

  return (
    <div className="p-4 space-y-5">
      {/* ── Sessão ── */}
      <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <Syringe className="w-4 h-4 text-slate-900" />
          <h4 className="font-semibold text-slate-900 text-sm">Visita de protocolo</h4>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Data do implante *</label>
            <input
              type="date"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={dados.session?.date || ''}
              onChange={(e) => mudarSessao('date', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Fazenda</label>
            <input
              type="text" placeholder="Nome da fazenda"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={dados.session?.farm || ''}
              onChange={(e) => mudarSessao('farm', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Cidade</label>
            <input
              type="text" placeholder="Cidade"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={dados.session?.city || ''}
              onChange={(e) => mudarSessao('city', e.target.value)}
            />
          </div>
          <EquipeDaEtapa
            rotuloResponsavel="Consultor(a)"
            laboratoryId={laboratoryId}
            responsavel={dados.session?.technician_id || dados.session?.technician_name
              ? { id: dados.session.technician_id, nome: dados.session.technician_name }
              : null}
            planejado={isInitialized ? equipe.planejado : null}
            ajudante={equipe.ajudante}
            onResponsavel={(id, nome) => {
              setDados(a => ({
                ...a, session: { ...a.session, technician_id: id, technician_name: nome },
              }));
              equipe.responsavelNaEquipe(id, nome);
            }}
            onAjudante={equipe.ajudanteNaEquipe}
          />
          <div className="col-span-2">
            <label className="block text-xs font-medium text-slate-700 mb-1">Protocolo</label>
            {/* Lista aberta: marca e esquema mudam, e o relatório é documento. */}
            <input
              type="text" list="protocolos-receptora" placeholder="P4 + BE — 8 dias"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={dados.session?.protocol_name || ''}
              onChange={(e) => mudarSessao('protocol_name', e.target.value)}
            />
            <datalist id="protocolos-receptora">
              {PROTOCOLOS_RECEPTORA.map(p => <option key={p} value={p} />)}
            </datalist>
          </div>
          {retirada && (
            <div className="col-span-2 flex items-end">
              <p className="text-xs text-slate-600">
                Retirada prevista: <strong>{retirada.split('-').reverse().join('/')}</strong>
                {' · '}A OPU é agendada oito dias depois do implante.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Receptoras ── */}
      <div>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h4 className="font-semibold text-slate-800 text-sm">
            Receptoras <span className="text-slate-500 font-normal">({ativas.length} ativas)</span>
          </h4>
          <div className="flex items-center gap-2">
            <button onClick={() => acrescentar(1)}
              className="flex items-center gap-1 text-xs text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded">
              <Plus className="w-3.5 h-3.5" /> Linha
            </button>
            <button onClick={() => acrescentar(10)}
              className="flex items-center gap-1 text-xs text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded">
              <Plus className="w-3.5 h-3.5" /> 10 linhas
            </button>
            <button onClick={() => setColando(true)}
              className="flex items-center gap-1 text-xs text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded">
              <ClipboardPaste className="w-3.5 h-3.5" /> Colar lista
            </button>
          </div>
        </div>

        {colando && (
          <div className="mb-2 p-3 border border-slate-300 rounded-lg bg-slate-50">
            <p className="text-xs text-slate-600 mb-1">Um brinco por linha.</p>
            <textarea
              rows={6} autoFocus
              className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
            />
            <div className="flex gap-2 mt-2">
              <button onClick={colarLista}
                className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700">
                Acrescentar {texto.split('\n').filter(l => l.trim()).length} receptoras
              </button>
              <button onClick={() => { setColando(false); setTexto(''); }}
                className="text-xs text-slate-600 hover:text-slate-900 underline">Cancelar</button>
            </div>
          </div>
        )}

        {dados.receptoras.length === 0 ? (
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center text-slate-500">
            <p className="text-sm">Nenhuma receptora lançada.</p>
            <p className="text-xs mt-1">Use "Colar lista" para trazer os brincos de uma vez.</p>
          </div>
        ) : (
          <div className="overflow-x-auto sm:overflow-visible sm:w-max sm:min-w-full rounded-lg border border-slate-200">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
                  <th className="px-2 py-1.5 text-left font-semibold w-10">#</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Receptora</th>
                  <th className="px-2 py-1.5 text-left font-semibold w-24">Raça</th>
                  <th className="px-2 py-1.5 text-center font-semibold w-16">ECC</th>
                  <th className="px-2 py-1.5 text-center font-semibold w-16">Ativa</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Obs.</th>
                  <th className="px-2 py-1.5 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dados.receptoras.map((r, i) => {
                  const { realce, ...linhaArrastavel } = arrastar.linha(i);
                  return (
                  <tr key={r.id} {...linhaArrastavel}
                    className={`${r.ativa === false ? 'bg-slate-50 text-slate-400' : ''} ${realce}`}>
                    <td className="px-1 py-1.5 text-slate-500 font-medium whitespace-nowrap">
                      <span className="inline-flex items-center gap-0.5">
                        <AlcaDeArrastar props={arrastar.alca(i, dados.receptoras.length)} rotulo="receptora" />
                        {i + 1}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 relative" style={{ minWidth: '150px' }}>
                      <BuscaDeAnimal
                        laboratoryId={laboratoryId}
                        valor={r.identifier}
                        onDigitar={(t) => digitarBrinco(r.id, t)}
                        onEscolher={(animal) => escolherAnimal(r.id, animal)}
                        filtro={(a) => a.category === 'Receptora' || a.category === 'Ambos'}
                        categoriaPadrao="Receptora"
                        clients={clients}
                        clientIdPadrao={service?.client_id}
                        placeholder="Brinco..."
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" placeholder="Raça"
                        className="w-full px-1.5 py-0.5 border border-slate-200 rounded text-xs"
                        value={r.breed || ''}
                        onChange={(e) => mudarReceptora(r.id, 'breed', e.target.value)} />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="number" step="0.5" min="1" max="5"
                        className="w-full px-1 py-0.5 border border-slate-200 rounded text-xs text-center"
                        value={r.ecc ?? ''}
                        onChange={(e) => mudarReceptora(r.id, 'ecc',
                          e.target.value === '' ? null : parseFloat(e.target.value))} />
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <input type="checkbox" className="h-4 w-4"
                        checked={r.ativa !== false}
                        onChange={(e) => mudarReceptora(r.id, 'ativa', e.target.checked)} />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text"
                        className="w-full px-1.5 py-0.5 border border-slate-200 rounded text-xs"
                        value={r.observations || ''}
                        onChange={(e) => mudarReceptora(r.id, 'observations', e.target.value)} />
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <button onClick={() => remover(r.id)} className="text-slate-400 hover:text-slate-700">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-slate-500 mt-1">
          Desmarcar <strong>Ativa</strong> tira a receptora do relatório de retirada — é o
          caso da vaca que saiu do lote entre o implante e a retirada.
        </p>
      </div>

      {/* ── Cronograma ── */}
      <div>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h4 className="font-semibold text-slate-800 text-sm">Cronograma hormonal</h4>
          <div className="flex items-center gap-2">
            <button onClick={acrescentarAplicacao}
              className="flex items-center gap-1 text-xs text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded">
              <Plus className="w-3.5 h-3.5" /> Aplicação
            </button>
            <button onClick={gerarCronograma} disabled={!dados.session?.date}
              title={dados.session?.date ? 'Substitui o cronograma pelo padrão de 8 dias' : 'Informe a data do implante'}
              className="text-xs text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded disabled:opacity-40">
              Gerar cronograma padrão
            </button>
          </div>
        </div>

        {dados.aplicacoes.length === 0 ? (
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center text-slate-500">
            <p className="text-sm">Sem cronograma.</p>
            <p className="text-xs mt-1">Ele sai nos relatórios de visita 1 e 2.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
                  <th className="px-2 py-1.5 text-left font-semibold w-32">Data</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Atividade</th>
                  <th className="px-2 py-1.5 text-left font-semibold w-24">Dose</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Responsável</th>
                  <th className="px-2 py-1.5 text-left font-semibold w-28">Momento</th>
                  <th className="px-2 py-1.5 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dados.aplicacoes.map(a => (
                  <tr key={a.id}>
                    <td className="px-2 py-1.5">
                      <input type="date"
                        className="w-full px-1.5 py-0.5 border border-slate-200 rounded text-xs"
                        value={a.data || ''}
                        onChange={(e) => mudarAplicacao(a.id, 'data', e.target.value)} />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" list="hormonios" placeholder="O que foi aplicado"
                        className="w-full px-1.5 py-0.5 border border-slate-200 rounded text-xs"
                        value={a.atividade || ''}
                        onChange={(e) => mudarAplicacao(a.id, 'atividade', e.target.value)} />
                    </td>
                    <td className="px-2 py-1.5">
                      {/* Texto: "1,00 ML" e "1 UND" convivem. */}
                      <input type="text" placeholder="1 ML"
                        className="w-full px-1.5 py-0.5 border border-slate-200 rounded text-xs"
                        value={a.dose || ''}
                        onChange={(e) => mudarAplicacao(a.id, 'dose', e.target.value)} />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text"
                        className="w-full px-1.5 py-0.5 border border-slate-200 rounded text-xs"
                        value={a.responsavel || ''}
                        onChange={(e) => mudarAplicacao(a.id, 'responsavel', e.target.value)} />
                    </td>
                    <td className="px-2 py-1.5">
                      <select
                        className="w-full px-1 py-0.5 border border-slate-200 rounded text-xs"
                        value={a.momento || 'inicio'}
                        onChange={(e) => mudarAplicacao(a.id, 'momento', e.target.value)}>
                        {MOMENTOS_DO_PROTOCOLO.map(m => (
                          <option key={m.id} value={m.id}>{m.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <button onClick={() => removerAplicacao(a.id)} className="text-slate-400 hover:text-slate-700">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <datalist id="hormonios">
          {HORMONIOS_SUGERIDOS.map(h => <option key={h} value={h} />)}
        </datalist>
        <p className="text-xs text-slate-500 mt-1">
          O momento <strong>Retirada</strong> é o que dá a data do relatório de retirada.
        </p>
      </div>

      {/* ── Fecho ── */}
      <div className="p-3 bg-white border border-slate-200 rounded-lg space-y-2">
        <label className="block text-xs font-medium text-slate-700">Observações da etapa</label>
        <textarea
          rows={2}
          className="w-full px-2 py-1 border border-slate-200 rounded text-xs"
          value={dados.observations || ''}
          onChange={(e) => setDados(a => ({ ...a, observations: e.target.value }))}
        />
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" className="h-4 w-4"
            checked={!!dados.completed}
            onChange={(e) => setDados(a => ({ ...a, completed: e.target.checked }))} />
          <span>{MARK.OK} Etapa concluída</span>
        </label>
      </div>
    </div>
  );
});

export default ProtocoloTab;
